import { Button, Icon } from '@goodlok/react-sdk'
import { Input, Select } from '@goodlok/ui'
import type { SelectProps } from '@goodlok/ui/src/components/Select'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, useMemo, useState } from 'react'
import { thunder } from '../app/data/thunder'
import type { AdminShiftTileProps } from './AdminShiftTile'
import styles from './DayDetail.module.sass'
import { DayShiftsGraph } from './DayShiftsGraph'
import { Note } from './Note'
import { TipsForm } from './TipsForm'

// @TODO would probably be better to use a hook to fetch all component data
export type DayDetailProps = {
	dayId: AdminShiftTileProps['day']['id']
	date: AdminShiftTileProps['day']['date']
	tipsCents: AdminShiftTileProps['day']['tipsCents']
	positions: AdminShiftTileProps['day']['shiftGroups']
	notes: AdminShiftTileProps['day']['notes']
}

export const DayDetail: FunctionComponent<DayDetailProps> = ({
	tipsCents,
	dayId,
	date,
	positions,
	notes,
}) => {
	const dateFormatted = new Date(date).toLocaleDateString('cs')

	const positionOptions: SelectProps['options'] = useMemo(() => {
		const positionOptions = [
			{ value: defaultOptionValue, label: 'Vyberte pozici:', disabled: true },
		]
		positions.forEach((position) => {
			if (!position.type || positionOptions.some((option) => option.value === position.type?.id)) {
				return
			}

			positionOptions.push({
				value: position.type.id,
				label: position.type.name ?? 'Neznámá pozice',
				disabled: false,
			})
		})
		return positionOptions
	}, [positions])

	return (
		<div className={styles.wrapper}>
			<div className={styles.column}>
				<h2 className={styles.date}>{dateFormatted}</h2>
				<DayShiftsGraph positions={positions} />
			</div>
			<div className={styles.column}>
				<TipsForm tipsCents={tipsCents} dayId={dayId} />

				<DayDetailNotes notes={notes} positionOptions={positionOptions} dayId={dayId} />
			</div>
		</div>
	)
}

const defaultOptionValue = 'default-option'

const DayDetailNotes: FunctionComponent<{
	dayId: AdminShiftTileProps['day']['id']
	notes: DayDetailProps['notes']
	positionOptions: SelectProps['options']
}> = ({ dayId, notes, positionOptions }) => {
	const [isNewFormVisible, setIsNewFormVisible] = useState(false)
	const [newNoteText, setNewNoteText] = useState<string>()
	const [newNoteRoleId, setNewNoteRoleId] = useState<string>()
	const queryClient = useQueryClient()

	const addMutation = useMutation(
		async () => {
			if (!newNoteText || !newNoteRoleId) {
				throw new Error('Missing note text or role id')
			}

			return thunder.admin('mutation')({
				updateTimetableDay: [
					{
						by: { id: dayId },
						data: {
							notes: [
								{
									create: {
										note: newNoteText,
										role: { connect: { id: newNoteRoleId } },
										order: notes.length + 1,
									},
								},
							],
						},
					},
					{
						ok: true,
						errorMessage: true,
					},
				],
			})
		},
		{
			onSuccess: (data) => {
				if (data.updateTimetableDay?.ok === false) {
					alert('Failed to add note')
					console.error(data.updateTimetableDay.errorMessage)
				}
				setNewNoteText(undefined)
				setNewNoteRoleId(undefined)
				setIsNewFormVisible(false)
				queryClient.invalidateQueries({ queryKey: ['adminPageData'] })
			},
		}
	)

	return (
		<div className={styles.notes}>
			<h3>Poznámky</h3>
			<div className={styles.notesItems}>
				{notes.map((note) => (
					<Note key={note.id} id={note.id} role={note.role?.name} text={note.note} isAdmin />
				))}
			</div>

			{isNewFormVisible ? (
				<form
					className={styles.newNoteForm}
					onSubmit={(event) => {
						event.preventDefault()
						if (!newNoteText || !newNoteRoleId) {
							return
						}
						addMutation.mutate()
					}}>
					<Input
						label="Poznámka"
						labelPosition="inside"
						textarea
						placeholder="Napište poznámku"
						value={newNoteText ?? ''}
						onChange={(event) => {
							setNewNoteText(event.target.value)
						}}
					/>
					<div className={styles.formBottomRow}>
						<Select
							options={positionOptions}
							value={newNoteRoleId ?? defaultOptionValue}
							onChange={(event) => {
								setNewNoteRoleId(event.target.value)
							}}
						/>
						<Button type="submit" variant="primary" disabled={!newNoteText || !newNoteRoleId}>
							Přidat
						</Button>
					</div>
				</form>
			) : (
				<div className={styles.addNoteButton}>
					<Button
						variant="seamless"
						type="button"
						onClick={() => setIsNewFormVisible(true)}
						bold
						uppercase
						startIcon={<Icon name="circlePlus" />}>
						Přidat poznámku
					</Button>
				</div>
			)}
		</div>
	)
}
